import React, { useMemo } from 'react';
import {
  Typography, Box, Paper, Divider, Chip, Table, TableBody,
  TableCell, TableContainer, TableHead, TableRow, Tooltip
} from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import {
  Warning as WarningIcon
} from '@material-ui/icons';
import {
  AREA_CATEGORIES, LANDSCAPE_CATEGORIES, HARDSCAPE_CATEGORIES,
  AREA_TYPE_LABELS, AREA_TYPE_COLORS, getAreaTypeLabel, getAreaTypeColor,
  PLANT_BEARING_TYPES, resolveAreaType, SERVICE_LABELS
} from './siteTakeoffConstants';

const useStyles = makeStyles((theme) => ({
  root: {
    padding: theme.spacing(1),
  },
  categoryHeader: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
    padding: '6px 8px',
    backgroundColor: '#f0f0f0',
    borderRadius: 4,
    marginTop: theme.spacing(1.5),
    marginBottom: theme.spacing(0.5),
  },
  categoryLabel: {
    fontWeight: 600,
    fontSize: '0.8rem',
    textTransform: 'uppercase',
    letterSpacing: 0.5,
  },
  subtotalRow: {
    backgroundColor: '#fafafa',
    '& td': { fontWeight: 600, fontSize: '0.8rem' },
  },
  areaRow: {
    cursor: 'pointer',
    '&:hover': { backgroundColor: 'rgba(0,0,0,0.04)' },
  },
  colorDot: {
    width: 12,
    height: 12,
    borderRadius: '50%',
    display: 'inline-block',
    marginRight: 6,
    border: '1px solid rgba(0,0,0,0.15)',
    verticalAlign: 'middle',
  },
  detailText: {
    fontSize: '0.7rem',
    color: '#888',
    marginLeft: 18,
  },
  modifierChip: {
    height: 18,
    fontSize: '0.65rem',
    marginLeft: 4,
  },
  totalsBox: {
    padding: theme.spacing(1.5),
    backgroundColor: '#e8f5e9',
    borderRadius: 4,
    marginTop: theme.spacing(2),
  },
  totalRow: {
    display: 'flex',
    justifyContent: 'space-between',
    padding: '2px 0',
  },
  emptyState: {
    textAlign: 'center',
    padding: theme.spacing(4),
    color: '#999',
  },
  table: {
    '& th': { padding: '4px 8px', fontSize: '0.75rem', fontWeight: 600 },
    '& td': { padding: '4px 8px', fontSize: '0.78rem' },
  },
}));

const formatSF = (num) => {
  const n = parseFloat(num) || 0;
  return n.toLocaleString('en-US', { maximumFractionDigits: 0 });
};

const formatLF = (num) => {
  const n = parseFloat(num) || 0;
  return n.toLocaleString('en-US', { maximumFractionDigits: 0 });
};

const getActiveModifiers = (modifiers) => {
  if (!modifiers) return [];
  const active = [];
  if (modifiers.access === 'obstructed') active.push('Obstructed');
  if (modifiers.slope === 'sloped') active.push('Sloped');
  if (modifiers.litterExposure === 'high') active.push('High Litter');
  if (modifiers.weedPressure === 'med') active.push('Med Weeds');
  if (modifiers.weedPressure === 'high') active.push('High Weeds');
  if (modifiers.limitedAccess) active.push('Limited Access');
  if (modifiers.blowRestrictions) active.push('Blow Restrictions');
  if (modifiers.irrigationIncluded === 'basic') active.push('Basic Irrigation');
  if (modifiers.irrigationIncluded === 'full') active.push('Full Irrigation');
  return active;
};

const SiteTakeoffSummary = ({ areas = [], onSelectArea }) => {
  const classes = useStyles();

  const summary = useMemo(() => {
    if (!areas.length) return null;

    // Group areas by category
    const categoryGroups = {};
    let totalLandscapeSF = 0;
    let totalHardscapeSF = 0;
    let totalSF = 0;
    let totalLF = 0;
    let totalTrees = 0;
    let modifierCount = 0;

    Object.entries(AREA_CATEGORIES).forEach(([catKey, cat]) => {
      const catAreas = areas.filter(a => {
        const resolved = resolveAreaType(a.areaType);
        return cat.types.includes(resolved);
      });

      if (catAreas.length === 0) return;

      let catSF = 0;
      let catLF = 0;

      catAreas.forEach(a => {
        const sf = parseFloat(a.area) || 0;
        const lf = parseFloat(a.perimeter) || 0;
        catSF += sf;
        catLF += lf;
        totalSF += sf;
        totalLF += lf;

        if (a.plantDensity?.treeCount) {
          totalTrees += a.plantDensity.treeCount;
        }

        const mods = getActiveModifiers(a.modifiers);
        if (mods.length > 0) modifierCount++;
      });

      if (LANDSCAPE_CATEGORIES.includes(catKey)) totalLandscapeSF += catSF;
      if (HARDSCAPE_CATEGORIES.includes(catKey)) totalHardscapeSF += catSF;

      // Group by areaType within category
      const typeGroups = {};
      catAreas.forEach(a => {
        const resolved = resolveAreaType(a.areaType);
        if (!typeGroups[resolved]) typeGroups[resolved] = [];
        typeGroups[resolved].push(a);
      });

      categoryGroups[catKey] = {
        ...cat,
        areas: catAreas,
        typeGroups,
        totalSF: catSF,
        totalLF: catLF,
      };
    });

    // Handle uncategorized areas
    const allCatTypes = Object.values(AREA_CATEGORIES).flatMap(c => c.types);
    const uncategorized = areas.filter(a => {
      const resolved = resolveAreaType(a.areaType);
      return !allCatTypes.includes(resolved);
    });

    if (uncategorized.length > 0) {
      let uncatSF = 0;
      let uncatLF = 0;
      uncategorized.forEach(a => {
        uncatSF += parseFloat(a.area) || 0;
        uncatLF += parseFloat(a.perimeter) || 0;
      });
      categoryGroups['UNCATEGORIZED'] = {
        label: 'Other',
        areas: uncategorized,
        typeGroups: { other: uncategorized },
        totalSF: uncatSF,
        totalLF: uncatLF,
      };
      totalSF += uncatSF;
      totalLF += uncatLF;
    }

    // Build group summaries
    const areaGroups = {};
    areas.forEach(a => {
      if (!a.groupId) return;
      if (!areaGroups[a.groupId]) areaGroups[a.groupId] = { name: a.groupName || 'Unnamed Group', areas: [] };
      areaGroups[a.groupId].areas.push(a);
    });

    return {
      categoryGroups,
      areaGroups,
      totalLandscapeSF,
      totalHardscapeSF,
      totalSF,
      totalLF,
      totalTrees,
      modifierCount,
      totalAcres: totalSF / 43560,
    };
  }, [areas]);

  if (!areas.length) {
    return (
      <div className={classes.emptyState}>
        <Typography variant="body2">
          No areas drawn yet. Use the drawing tools to add areas to the map.
        </Typography>
      </div>
    );
  }

  if (!summary) return null;

  return (
    <div className={classes.root}>
      <Typography variant="subtitle2" gutterBottom>
        Site Takeoff Summary
      </Typography>

      <TableContainer>
        <Table size="small" className={classes.table}>
          <TableHead>
            <TableRow>
              <TableCell>Area</TableCell>
              <TableCell align="right">SF</TableCell>
              <TableCell align="right">LF</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {Object.entries(summary.categoryGroups).map(([catKey, cat]) => (
              <React.Fragment key={catKey}>
                {/* Category header */}
                <TableRow>
                  <TableCell colSpan={3} style={{ padding: 0 }}>
                    <div className={classes.categoryHeader}>
                      <Typography className={classes.categoryLabel}>
                        {cat.label}
                      </Typography>
                      <Chip
                        size="small"
                        label={`${cat.areas.length}`}
                        style={{ height: 18, fontSize: '0.7rem' }}
                      />
                    </div>
                  </TableCell>
                </TableRow>

                {/* Type groups within category */}
                {Object.entries(cat.typeGroups).map(([typeKey, typeAreas]) => {
                  const typeSF = typeAreas.reduce((s, a) => s + (parseFloat(a.area) || 0), 0);
                  const typeLF = typeAreas.reduce((s, a) => s + (parseFloat(a.perimeter) || 0), 0);
                  const label = getAreaTypeLabel(typeKey);
                  const color = getAreaTypeColor(typeKey);
                  const hasServices = typeAreas.some(a => (a.servicesRequired || []).length > 0);
                  const showDetail = typeAreas.length > 1 || PLANT_BEARING_TYPES.includes(typeKey) || hasServices;

                  return (
                    <React.Fragment key={typeKey}>
                      {/* Type summary row */}
                      <TableRow className={classes.areaRow}>
                        <TableCell>
                          <span className={classes.colorDot} style={{ backgroundColor: color }} />
                          {label}
                          {typeAreas.length > 1 && (
                            <span style={{ color: '#999', marginLeft: 4 }}>
                              (x{typeAreas.length})
                            </span>
                          )}
                        </TableCell>
                        <TableCell align="right">{formatSF(typeSF)}</TableCell>
                        <TableCell align="right">{formatLF(typeLF)}</TableCell>
                      </TableRow>

                      {/* Detail rows for individual areas */}
                      {showDetail && typeAreas.map((a, idx) => {
                        const mods = getActiveModifiers(a.modifiers);
                        const isPlantBearing = PLANT_BEARING_TYPES.includes(resolveAreaType(a.areaType));
                        const density = a.plantDensity || {};

                        return (
                          <TableRow
                            key={a._id || a.id || idx}
                            className={classes.areaRow}
                            onClick={() => onSelectArea && onSelectArea(a)}
                          >
                            <TableCell colSpan={3} style={{ paddingLeft: 24 }}>
                              <Typography className={classes.detailText}>
                                {a.name || `Area ${idx + 1}`}: {formatSF(a.area)} SF
                                {isPlantBearing && density.treeCount > 0 && (
                                  <span> ({density.treeCount} trees</span>
                                )}
                                {isPlantBearing && density.shrubDensity && density.shrubDensity !== 'none' && (
                                  <span>
                                    {density.treeCount > 0 ? ', ' : ' ('}
                                    {density.shrubDensity} shrubs
                                  </span>
                                )}
                                {isPlantBearing && (density.treeCount > 0 || (density.shrubDensity && density.shrubDensity !== 'none')) && (
                                  <span>)</span>
                                )}
                                {mods.map(m => (
                                  <Chip
                                    key={m}
                                    size="small"
                                    label={m}
                                    className={classes.modifierChip}
                                    variant="outlined"
                                    icon={<WarningIcon style={{ fontSize: 12 }} />}
                                  />
                                ))}
                              </Typography>
                              {(a.servicesRequired || []).length > 0 && (
                                <Typography style={{ fontSize: '0.65rem', color: '#1976d2', marginLeft: 18, marginTop: 1 }}>
                                  {a.servicesRequired.map(s => SERVICE_LABELS[s] || s).join(' · ')}
                                </Typography>
                              )}
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </React.Fragment>
                  );
                })}

                {/* Category subtotal */}
                <TableRow className={classes.subtotalRow}>
                  <TableCell>Subtotal</TableCell>
                  <TableCell align="right">{formatSF(cat.totalSF)}</TableCell>
                  <TableCell align="right">{formatLF(cat.totalLF)}</TableCell>
                </TableRow>
              </React.Fragment>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Group Summaries */}
      {Object.keys(summary.areaGroups).length > 0 && (
        <Box mt={1.5} mb={0.5}>
          <Typography variant="caption" style={{ fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.5, color: '#7b1fa2' }}>
            Groups
          </Typography>
          {Object.entries(summary.areaGroups).map(([gid, group]) => {
            const gSF = group.areas.reduce((s, a) => s + (parseFloat(a.area) || 0), 0);
            const gLF = group.areas.reduce((s, a) => s + (parseFloat(a.perimeter) || 0), 0);
            return (
              <Box key={gid} px={1} py={0.5} mb={0.5}
                style={{ backgroundColor: '#f3e5f5', borderRadius: 4, borderLeft: '3px solid #7b1fa2' }}
              >
                <Box display="flex" justifyContent="space-between" alignItems="center">
                  <Typography style={{ fontSize: '0.78rem', fontWeight: 600, color: '#4a148c' }}>
                    {group.name}
                  </Typography>
                  <Typography style={{ fontSize: '0.75rem', color: '#7b1fa2' }}>
                    {formatSF(gSF)} SF | {formatLF(gLF)} LF
                  </Typography>
                </Box>
                {group.areas.map((a, idx) => (
                  <Typography key={a._id || a.id || idx} style={{ fontSize: '0.7rem', color: '#666', paddingLeft: 8 }}>
                    {a.name || getAreaTypeLabel(a.areaType)}: {formatSF(a.area)} SF
                  </Typography>
                ))}
              </Box>
            );
          })}
        </Box>
      )}

      {/* Grand Totals */}
      <Paper className={classes.totalsBox} elevation={0}>
        <Typography variant="subtitle2" gutterBottom>Totals</Typography>
        <div className={classes.totalRow}>
          <Typography variant="body2">Landscape</Typography>
          <Typography variant="body2"><strong>{formatSF(summary.totalLandscapeSF)} SF</strong></Typography>
        </div>
        <div className={classes.totalRow}>
          <Typography variant="body2">Hardscape</Typography>
          <Typography variant="body2"><strong>{formatSF(summary.totalHardscapeSF)} SF</strong></Typography>
        </div>
        <Divider style={{ margin: '4px 0' }} />
        <div className={classes.totalRow}>
          <Typography variant="body2">Total Property</Typography>
          <Typography variant="body2">
            <strong>{formatSF(summary.totalSF)} SF</strong>
            <span style={{ color: '#666', marginLeft: 8 }}>
              ({summary.totalAcres.toFixed(2)} ac)
            </span>
          </Typography>
        </div>
        <div className={classes.totalRow}>
          <Typography variant="body2">Total Perimeter</Typography>
          <Typography variant="body2"><strong>{formatLF(summary.totalLF)} LF</strong></Typography>
        </div>
        {summary.totalTrees > 0 && (
          <div className={classes.totalRow}>
            <Typography variant="body2">Trees</Typography>
            <Typography variant="body2"><strong>{summary.totalTrees}</strong></Typography>
          </div>
        )}
        {summary.modifierCount > 0 && (
          <div className={classes.totalRow}>
            <Typography variant="body2">Areas w/ Modifiers</Typography>
            <Typography variant="body2">
              <strong>{summary.modifierCount}</strong>
            </Typography>
          </div>
        )}
      </Paper>
    </div>
  );
};

export default React.memo(SiteTakeoffSummary);
